import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Send, Sparkles, Cpu, KeyRound, AlertTriangle, Square, ImagePlus, X, Wand2, HelpCircle,
  RotateCw, Pencil, Clock, Images, Check, Loader2,
} from "lucide-react";
import LibraryDialog from "./LibraryDialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";

/**
 * Silence long enough to be worth offering a way out of.
 *
 * Generous on purpose: a model can think for a long time before its first
 * token, and a prompt that says "stuck" the moment output pauses trains people
 * to cancel runs that were about to succeed.
 */
// Phased builds have legitimately quiet stretches — drawing four photographs
// emits nothing for as long as the slowest one takes. 45s was tuned for a run
// that streamed tokens continuously and now cries wolf on a healthy build.
const STALL_AFTER_MS = 90_000;

/** Mirrors the Worker's limits, so a rejection is caught before the upload. */
const MAX_ATTACHMENTS = 4;
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read that file"));
    reader.readAsDataURL(file);
  });
}

export default function ChatPanel({
  messages, streamingText, isStreaming, currentModel, onSend, onStop,
  models, allowed, mode, setMode, model, setModel, prompt, setPrompt, hasKeys,
  autopilot, setAutopilot, buildProgress, target, setTarget,
}) {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);

  /**
   * Attaches an image chosen from the library.
   *
   * It arrives as a data URL, the same shape `addFiles` produces, so the rest
   * of the composer does not need to know where an attachment came from.
   * `previewUrl` is the data URL itself rather than an object URL — there is
   * no File to revoke, and the unmount cleanup tolerates both.
   */
  const attachFromLibrary = useCallback((asset) => {
    setAttachments((current) => {
      if (current.length >= MAX_ATTACHMENTS) {
        toast.error(`At most ${MAX_ATTACHMENTS} images per message`);
        return current;
      }
      if (current.some((a) => a.libraryId === asset.id)) return current;
      return [...current, {
        id: `lib-${asset.id}`,
        libraryId: asset.id,
        name: asset.name,
        data: asset.data,
        previewUrl: asset.data,
      }];
    });
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamingText]);

  // A run that has produced nothing for a while. The generation is not
  // necessarily dead — a model can think for a long time before its first
  // token — so this offers a way out rather than acting on its own.
  const [stalled, setStalled] = useState(false);
  const lastOutputAt = useRef(Date.now());

  // Seconds since this run began, for the waiting state below.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!isStreaming) { setElapsed(0); return undefined; }
    const started = Date.now();
    const tick = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(tick);
  }, [isStreaming]);

  /**
   * Whether the build has anything structural to report yet.
   *
   * A one-pass build has no file plan by design, so keying the card off the
   * plan alone hid the photographs and the storage on the path that is now the
   * common one.
   */
  const hasProgress =
    (buildProgress?.planned?.length ?? 0) > 0 ||
    (buildProgress?.imageCount ?? 0) > 0 ||
    (buildProgress?.collections?.length ?? 0) > 0 ||
    Boolean(buildProgress?.reviewing || buildProgress?.reviewChanged);

  // `buildProgress` counts as output. A multi-pass build streams the plan and
  // then goes quiet for minutes while it writes files — the bodies never reach
  // the token stream — so without this a healthy run is reported as stalled the
  // moment it starts doing the actual work.
  useEffect(() => {
    lastOutputAt.current = Date.now();
    setStalled(false);
  }, [streamingText, isStreaming, buildProgress]);

  useEffect(() => {
    if (!isStreaming) {
      setStalled(false);
      return undefined;
    }
    const timer = setInterval(() => {
      if (Date.now() - lastOutputAt.current > STALL_AFTER_MS) setStalled(true);
    }, 4000);
    return () => clearInterval(timer);
  }, [isStreaming]);

  const lastUserIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === "user") return i;
    return -1;
  }, [messages]);

  const retry = (message) => {
    if (isStreaming) return;
    onSend(message.content, message.attachments ?? []);
  };

  // Editing puts the text back in the composer rather than rewriting history.
  // The turn is already persisted server-side, and quietly changing what the
  // user is shown they said would not match what the model was given.
  const editInComposer = (message) => {
    if (isStreaming) return;
    setPrompt(message.content);
    inputRef.current?.focus();
  };

  // Object URLs are revoked on unmount rather than per-render, so a thumbnail
  // is never revoked while it is still on screen.
  useEffect(() => () => attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl)), [attachments]);

  const addFiles = useCallback(async (files) => {
    const images = [...files].filter((f) => ACCEPTED_TYPES.includes(f.type));
    if (images.length === 0) {
      toast.error("Only PNG, JPEG, WebP and GIF images can be attached");
      return;
    }

    const room = MAX_ATTACHMENTS - attachments.length;
    if (room <= 0) {
      toast.error(`At most ${MAX_ATTACHMENTS} images per message`);
      return;
    }

    const accepted = [];
    for (const file of images.slice(0, room)) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`${file.name || "That image"} is larger than 5 MB`);
        continue;
      }
      try {
        const dataUrl = await readAsDataUrl(file);
        accepted.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          name: file.name || "pasted image",
          data: dataUrl,
          previewUrl: URL.createObjectURL(file),
        });
      } catch {
        toast.error("Could not read that image");
      }
    }

    if (accepted.length > 0) setAttachments((current) => [...current, ...accepted]);
    if (images.length > room) toast.message(`Only the first ${room} image(s) were attached`);
  }, [attachments.length]);

  const onPaste = (event) => {
    const files = [...(event.clipboardData?.files ?? [])];
    if (files.length > 0) {
      event.preventDefault();
      addFiles(files);
    }
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer?.files?.length) addFiles(event.dataTransfer.files);
  };

  const removeAttachment = (id) => {
    setAttachments((current) => {
      const target = current.find((a) => a.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return current.filter((a) => a.id !== id);
    });
  };

  const send = () => {
    if ((!prompt.trim() && attachments.length === 0) || isStreaming) return;
    onSend(prompt.trim(), attachments.map((a) => a.data));
    setAttachments([]);
  };

  const canSend = (prompt.trim() || attachments.length > 0) && hasKeys !== false;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Router header */}
      <div className="h-11 shrink-0 border-b border-border flex items-center justify-between px-3 gap-2">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Sparkles className="w-4 h-4 text-kinetic" />
          <span className="font-mono text-xs uppercase tracking-wider">Generate</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="mode-select" className="h-7 w-[92px] bg-card border-border font-mono text-[11px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="auto" className="font-mono text-xs">auto</SelectItem>
              <SelectItem value="manual" className="font-mono text-xs">manual</SelectItem>
            </SelectContent>
          </Select>
          {mode === "manual" && (
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger data-testid="model-select" className="h-7 w-[150px] bg-card border-border font-mono text-[11px]">
                <SelectValue placeholder="model" />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {models.map((m) => (
                  <SelectItem key={m.id} value={m.id} disabled={!allowed.includes(m.id)}
                    className="font-mono text-xs">
                    {m.label}
                    {!m.vision && " · text only"}
                    {!allowed.includes(m.id) && " 🔒"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-5">
        {hasKeys === false && (
          <div data-testid="no-keys-banner" className="border border-kinetic/40 bg-kinetic/10 rounded-lg p-4">
            <div className="flex items-center gap-2 text-kinetic mb-1.5">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-app text-sm font-semibold">No API keys configured</span>
            </div>
            <p className="text-xs text-zinc-300 mb-3">Catalina uses your own provider keys (BYOK). Add at least one to start generating.</p>
            <Button data-testid="banner-add-keys" size="sm" onClick={() => navigate("/settings")}
              className="bg-white text-black hover:bg-zinc-200 h-8">
              <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Add API keys
            </Button>
          </div>
        )}
        {messages.length === 0 && !isStreaming && (
          <div className="text-center py-10">
            <p className="font-app text-lg text-white mb-1">What are we building?</p>
            <p className="text-sm text-muted-foreground">
              Describe your app, or paste a screenshot of something that is broken.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <MessageBubble
            key={m.id || i}
            m={m}
            models={models}
            canReplay={i === lastUserIndex && !isStreaming}
            onRetry={() => retry(m)}
            onEdit={() => editInComposer(m)}
          />
        ))}
        {isStreaming && (
          <div className="space-y-1.5">
            {currentModel && (
              <div className="flex items-center gap-1.5 text-[11px] font-mono text-kinetic">
                <Cpu className="w-3 h-3" /> {currentModel.label} · {currentModel.mode}
                {currentModel.autopilot && " · autopilot"}
              </div>
            )}
            {/*
              The gap between choosing a model and its first token. A large
              prompt against a frontier model can sit there for the better part
              of a minute, and a bare cursor is indistinguishable from a hang —
              which is what people were stopping perfectly good runs over. The
              elapsed count is the part that reassures: something is moving.
            */}
            {stripFiles(streamingText) !== "" && (
              <div data-testid="streaming-output" className="text-sm text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed">
                {stripFiles(streamingText)}
              </div>
            )}
            {/*
              Shown beside the text, not instead of it. The first version only
              appeared while the output was empty, which left the longest wait
              of all unlabelled: the brief has streamed, the plan has not
              arrived, and all that is on screen is a blinking cursor after a
              paragraph. Indistinguishable from finished, or from hung.
            */}
            {!buildProgress?.planned?.length && (
              <div data-testid="thinking" className="flex items-center gap-2 text-sm font-mono text-zinc-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-kinetic shrink-0" />
                <span>
                  {streamPhase(streamingText)}
                  <span className="thinking-dots" />
                </span>
                {elapsed >= 3 && <span className="text-zinc-600 tabular-nums">{elapsed}s</span>}
              </div>
            )}
          </div>
        )}
        {/*
          Shown for anything worth reporting, not only for a planned build. The
          card used to hang off the file list, so a one-pass build — which has
          no plan by design — reported neither the photographs being drawn
          alongside it nor the storage it had just been given.
        */}
        {isStreaming && hasProgress && (
          <div data-testid="build-progress" className="border border-border rounded-lg bg-card p-3">
            {buildProgress.planned?.length > 0 && (
              <>
                <div className="font-app text-xs font-semibold text-zinc-300 mb-2">
                  Building {buildProgress.planned.length} files
                </div>
                <ul className="space-y-1">
                  {buildProgress.planned.map((file) => {
                    const done = buildProgress.done?.includes(file.path);
                    const active = buildProgress.current?.path === file.path;
                    return (
                      <li key={file.path} className="flex items-center gap-2 font-mono text-xs">
                        {done ? (
                          <Check className="w-3 h-3 text-green-400 shrink-0" />
                        ) : active ? (
                          <Loader2 className="w-3 h-3 animate-spin text-kinetic shrink-0" />
                        ) : (
                          <span className="w-3 h-3 shrink-0 text-zinc-600 text-center leading-3">·</span>
                        )}
                        <span className={done ? "text-zinc-400" : active ? "text-zinc-200" : "text-zinc-600"}>
                          {file.path}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
            {buildProgress.imageCount > 0 && (
              <div className="mt-2.5 pt-2.5 border-t border-border flex items-center gap-2 font-mono text-xs first:mt-0 first:pt-0 first:border-t-0">
                {buildProgress.imagesDone >= buildProgress.imageCount ? (
                  <Check className="w-3 h-3 text-green-400 shrink-0" />
                ) : (
                  <Loader2 className="w-3 h-3 animate-spin text-kinetic shrink-0" />
                )}
                <span className="text-zinc-400">
                  Photographs {buildProgress.imagesDone || 0}/{buildProgress.imageCount}
                </span>
              </div>
            )}
            {(buildProgress.reviewing || buildProgress.reviewChanged) && (
              <div className="mt-2.5 pt-2.5 border-t border-border first:mt-0 first:pt-0 first:border-t-0">
                {buildProgress.reviewing ? (
                  <div className="flex items-center gap-2 font-mono text-xs text-zinc-300">
                    <Loader2 className="w-3 h-3 animate-spin text-kinetic shrink-0" />
                    Reviewing the design
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 font-mono text-xs text-zinc-400">
                      <Check className="w-3 h-3 text-green-400 shrink-0" />
                      {buildProgress.reviewChanged.length === 0
                        ? "Design review — nothing to change"
                        : `Design review — reworked ${buildProgress.reviewChanged.join(", ")}`}
                    </div>
                    {buildProgress.reviewNote && (
                      <p className="mt-1.5 text-xs text-zinc-500 leading-relaxed">
                        {buildProgress.reviewNote}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
            {buildProgress.collections?.length > 0 && (
              <div className="mt-2.5 pt-2.5 border-t border-border first:mt-0 first:pt-0 first:border-t-0">
                <div className="font-app text-xs font-semibold text-zinc-300 mb-1.5">Storage</div>
                <ul className="space-y-0.5">
                  {buildProgress.collections.map((c) => (
                    <li key={c.collection} className="font-mono text-xs text-zinc-400">
                      {c.collection}
                      {/*
                        Public and owner-scoped grants are listed apart, and the
                        public ones first. They are not the same promise: one is
                        what anyone who opens the page may do, the other is what
                        a signed-in visitor may do to what they wrote. Merging
                        them into one list would hide the difference that
                        matters most on a collection holding names and emails.
                      */}
                      <span className="text-zinc-600">
                        {" · "}
                        {["read", "create", "update", "delete"]
                          .filter((verb) => c[`can_${verb}`])
                          .join(", ") || "nothing public"}
                      </span>
                      {["create", "read", "update", "delete"].some((v) => c[`own_${v}`]) && (
                        <span className="text-kinetic">
                          {" · signed in: "}
                          {["create", "read", "update", "delete"]
                            .filter((verb) => c[`own_${verb}`])
                            .join(", ")}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        {isStreaming && stalled && (
          <div data-testid="stall-notice" className="border border-amber-500/40 bg-amber-500/10 rounded-lg p-3">
            <div className="flex items-center gap-2 text-amber-400 mb-1">
              <Clock className="w-4 h-4" />
              <span className="font-app text-sm font-semibold">Nothing for a while</span>
            </div>
            <p className="text-xs text-zinc-300 mb-2.5">
              The model has sent nothing for {Math.round(STALL_AFTER_MS / 1000)} seconds. It may still
              be working — files are saved as they arrive, so stopping keeps whatever has landed.
            </p>
            <Button data-testid="stall-stop" size="sm" variant="outline" onClick={onStop}
              className="border-border h-7 text-xs">
              <Square className="w-3 h-3 mr-1.5" /> Stop, then retry
            </Button>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="shrink-0 border-t border-border p-3">
        {/*
          A page, or a real project that gets compiled.

          Chosen here rather than by the model on purpose. Getting it wrong
          costs a whole build, and the person typing the request is the one who
          knows which they meant — a brochure and a booking system are not the
          same ask, however similarly they are worded.
        */}
        <div className="flex items-center gap-1 mb-2 p-0.5 rounded-md bg-zinc-900/60 border border-border">
          {[
            { id: "site", label: "Site", hint: "One page, or a few. No build step — ready in about a minute." },
            { id: "app", label: "App", hint: "A real Vite and React project with components and state, compiled before you see it. Slower, and a codebase you can grow." },
          ].map((option) => (
            <TooltipProvider key={option.id} delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    data-testid={`target-${option.id}`}
                    onClick={() => setTarget?.(option.id)}
                    disabled={isStreaming}
                    className={`flex-1 rounded px-2 py-1 font-mono text-[11px] uppercase tracking-wider transition-colors disabled:opacity-50 ${
                      target === option.id
                        ? "bg-zinc-800 text-zinc-100"
                        : "text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    {option.label}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs bg-popover border-border">
                  <p className="text-xs">{option.hint}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>

        {/* Autopilot */}
        <div className="flex items-center justify-between mb-2 px-1">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 cursor-help">
                  <Wand2 className={`w-3.5 h-3.5 ${autopilot ? "text-kinetic" : "text-zinc-600"}`} />
                  <span className={`font-mono text-[11px] uppercase tracking-wider ${autopilot ? "text-zinc-300" : "text-zinc-600"}`}>
                    Autopilot
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs bg-popover border-border">
                <p className="text-xs">
                  {autopilot
                    ? "Catalina builds the whole app in one pass, deciding anything you left open and telling you what it chose. It only stops to ask if the request could mean two incompatible things."
                    : "Catalina builds what you asked and flags anything ambiguous instead of deciding for you."}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Switch
            data-testid="autopilot-toggle"
            checked={autopilot}
            onCheckedChange={setAutopilot}
            aria-label="Autopilot"
          />
        </div>

        {/* Attachment thumbnails */}
        {attachments.length > 0 && (
          <div data-testid="attachment-strip" className="flex flex-wrap gap-2 mb-2">
            {attachments.map((a) => (
              <div key={a.id} className="relative group">
                <img src={a.previewUrl} alt={a.name}
                  className="h-14 w-14 object-cover rounded border border-border" />
                <button
                  data-testid={`remove-attachment-${a.id}`}
                  onClick={() => removeAttachment(a.id)}
                  aria-label={`Remove ${a.name}`}
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-zinc-900 border border-border
                             flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-500
                             focus-visible:outline focus-visible:outline-2 focus-visible:outline-kinetic transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`relative border rounded-lg bg-card transition-colors ${
            dragging ? "border-kinetic" : "border-border focus-within:border-zinc-500"
          }`}
        >
          <Textarea
            data-testid="chat-input"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onPaste={onPaste}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
            placeholder={dragging ? "Drop the image here…" : "Build a landing page for a coffee brand…"}
            className="min-h-[72px] max-h-40 resize-none bg-transparent border-0 focus-visible:ring-0 text-sm pr-20"
          />

          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(",")}
            multiple
            hidden
            data-testid="attachment-input"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />

          <Button
            data-testid="library-btn"
            onClick={() => setLibraryOpen(true)}
            disabled={isStreaming || attachments.length >= MAX_ATTACHMENTS}
            size="icon"
            variant="ghost"
            aria-label="Attach from the library"
            title="Attach from the library"
            className="absolute bottom-2 right-20 h-8 w-8 text-zinc-500 hover:text-white"
          >
            <Images className="w-4 h-4" />
          </Button>

          <Button
            data-testid="attach-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={isStreaming || attachments.length >= MAX_ATTACHMENTS}
            size="icon"
            variant="ghost"
            aria-label="Attach an image"
            className="absolute bottom-2 right-11 h-8 w-8 text-zinc-500 hover:text-white"
          >
            <ImagePlus className="w-4 h-4" />
          </Button>

          {isStreaming ? (
            <Button data-testid="stop-prompt-btn" onClick={onStop}
              size="icon" className="absolute bottom-2 right-2 h-8 w-8 bg-zinc-700 hover:bg-zinc-600 text-white">
              <Square className="w-3.5 h-3.5" fill="currentColor" />
            </Button>
          ) : (
            <Button data-testid="send-prompt-btn" onClick={send} disabled={!canSend}
              size="icon" className="absolute bottom-2 right-2 h-8 w-8 bg-kinetic hover:bg-kinetic/90 text-white">
              <Send className="w-4 h-4" />
            </Button>
          )}
        </div>

        <p className="text-[10px] text-zinc-600 font-mono mt-1.5 px-1">
          {hasKeys === false
            ? "Add an API key in Settings to generate"
            : "⌘/Ctrl + Enter to send · paste or drop a screenshot · BYOK"}
        </p>
      </div>

      <LibraryDialog open={libraryOpen} onOpenChange={setLibraryOpen} onPick={attachFromLibrary} />
    </div>
  );
}

function MessageBubble({ m, models, canReplay = false, onRetry, onEdit }) {
  if (m.role === "user") {
    // Attachments are only held for turns sent in this session; a message
    // loaded from history knows how many there were but not what they were.
    const losesImages = m.attachmentCount > 0 && !(m.attachments?.length > 0);

    return (
      <div className="flex flex-col items-end gap-1">
        <div className="max-w-[90%] bg-secondary rounded-lg px-3.5 py-2.5 text-sm">
          {m.content}
          {m.attachmentCount > 0 && (
            <div className="mt-1.5 flex items-center gap-1 text-[10px] font-mono text-zinc-400">
              <ImagePlus className="w-3 h-3" />
              {m.attachmentCount} image{m.attachmentCount > 1 ? "s" : ""} attached
            </div>
          )}
        </div>
        {canReplay && (
          <div className="flex items-center gap-1">
            <button
              data-testid="message-edit"
              onClick={onEdit}
              title="Put this back in the composer to change it"
              className="flex items-center gap-1 px-1.5 py-1 rounded text-[11px] font-mono text-zinc-500
                         hover:text-white focus-visible:outline focus-visible:outline-2
                         focus-visible:outline-kinetic transition-colors"
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
            <button
              data-testid="message-retry"
              onClick={onRetry}
              title={losesImages
                ? "Send this prompt again. The images are not re-attached — they are not kept after a reload."
                : "Send this prompt again"}
              className="flex items-center gap-1 px-1.5 py-1 rounded text-[11px] font-mono text-zinc-500
                         hover:text-white focus-visible:outline focus-visible:outline-2
                         focus-visible:outline-kinetic transition-colors"
            >
              <RotateCw className="w-3 h-3" /> Retry{losesImages ? " (no images)" : ""}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Autopilot decided it could not proceed and asked instead of guessing.
  if (m.isQuestion) {
    return (
      <div data-testid="autopilot-question" className="border border-amber-500/40 bg-amber-500/10 rounded-lg p-3.5">
        <div className="flex items-center gap-2 text-amber-400 mb-1.5">
          <HelpCircle className="w-4 h-4" />
          <span className="font-app text-sm font-semibold">Catalina needs one answer</span>
        </div>
        <p className="text-sm text-zinc-200 whitespace-pre-wrap">{m.content}</p>
        <p className="text-[11px] text-zinc-500 mt-2">Reply below and it will build the rest on its own.</p>
      </div>
    );
  }

  const model = models.find((x) => x.id === m.model_used);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 text-[11px] font-mono">
        <span className="text-kinetic">{model?.label || m.model_used || "assistant"}</span>
        {typeof m.cost_usd === "number" && m.cost_usd > 0 && (
          <Badge variant="outline" className="border-border text-[10px] font-mono text-green-400 py-0">
            ${m.cost_usd.toFixed(5)}
          </Badge>
        )}
      </div>
      <div className="text-sm text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed">{stripFiles(m.content)}</div>
    </div>
  );
}

/**
 * Shows the brief and hides the protocol around it.
 *
 * A planning pass streams the brief followed by ===PLAN=== and ===DATA===
 * blocks. Those are instructions to the server, not prose for the reader, and
 * they were arriving in the chat as raw markers. The plan is rendered properly
 * from the `plan` event instead; here it is simply cut.
 */
/**
 * What the run is doing, read off the stream itself.
 *
 * "Planning the build" was accurate when every first build was planned. It is
 * not any more: the common ending writes the whole app in the same response as
 * the brief, and the label sat on "planning" for the entire time it was being
 * written — which is precisely the wait the label exists to explain.
 *
 * Checked from the latest phase backwards, because the markers accumulate: a
 * response that has reached its files still contains its image block.
 */
function streamPhase(text) {
  if (!text) return "Thinking";
  if (text.includes("===FILE:")) return "Writing the app";
  if (text.includes("===PLAN===")) return "Planning the pages";
  if (text.includes("===IMAGES===")) return "Briefing the photographs";
  return "Designing";
}

function stripFiles(text) {
  if (!text) return "";

  const fileCount = (text.match(/===FILE:/g) || []).length;
  const cut = ["===FILE:", "===PLAN===", "===DATA===", "===IMAGES==="]
    .map((marker) => text.indexOf(marker))
    .filter((index) => index !== -1);

  const summary = cut.length ? text.slice(0, Math.min(...cut)) : text;
  const suffix = fileCount ? `\n\n▸ generated ${fileCount} file${fileCount > 1 ? "s" : ""}` : "";
  return summary.trim() + suffix;
}
